import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useWallet } from "../context/WalletContext.jsx";
import { useGeofence } from "../hooks/useGeofence.js";

/**
 * Route gate for everything reachable from the sidebar. If the visitor
 * has no connected wallet we:
 *   1. Pop the wallet-connect modal (remembering the path they tried to
 *      reach via WalletContext's `pendingDestination` so the post-connect
 *      navigate lands them back here).
 *   2. Redirect to Home so the page they aren't allowed to see never
 *      gets rendered.
 *
 * During the brief moment WalletContext is silently restoring a saved
 * session (window.ethereum `eth_accounts` or a WC v2 session in
 * localStorage), we render nothing rather than flashing a redirect —
 * otherwise a refresh on a protected page would bounce to Home for a
 * frame before the restored address comes back.
 */
export default function ProtectedOutlet() {
    const { address, isRestoring, requestConnect } = useWallet();
    const { country, blocked, isLoading: geoLoading } = useGeofence();
    const location = useLocation();

    useEffect(() => {
        if (!isRestoring && !geoLoading && !blocked && !address) {
            // Remember the intended destination; WalletConnect.jsx's
            // handleSelectWallet picks this up after a successful
            // connect and navigates there.
            requestConnect(location.pathname + location.search);
        }
    }, [isRestoring, geoLoading, blocked, address, requestConnect, location.pathname, location.search]);

    if (isRestoring || geoLoading) return null;
    if (blocked) return <GeofenceBlocked country={country} />;
    if (!address) return <Navigate to="/" replace />;
    return <Outlet />;
}

function GeofenceBlocked({ country }) {
    return (
        <div className="flex min-h-[80vh] items-center justify-center px-4">
            <div className="max-w-md rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center">
                <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#C93030]/20 text-[#FBC206]" aria-hidden="true">
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </svg>
                </div>
                <h2 className="mt-4 font-manrope text-[18px] font-bold text-[#e6c985]">
                    Service Not Available
                </h2>
                <p className="mt-2 text-[12px] leading-relaxed text-[#e6c985]/80">
                    GILDer is not currently available in your jurisdiction
                    {country && country !== "DEV" ? ` (${country})` : ""}.
                    Compliance review is ongoing — please check back later.
                </p>
            </div>
        </div>
    );
}
